import React from 'react';
import PropTypes from 'prop-types';

import { StructuredTextPropTypes } from 'dpl/prismic/utils/customPropTypes';
import { get } from 'dpl/shared/utils/object';
import { getFormattedDateTime } from 'dpl/shared/utils/date';
import { isGreaterThanBreakpoint } from 'dpl/util/grid';
import { getArticlePath } from 'dpl/prismic/utils';
import SmartImage from 'dpl/components/SmartImage';
import Icon from 'dpl/common/components/Icon';
import RichText from 'dpl/prismic/components/RichText';

export default function FeaturedWebinars({ sliceData }) {
  const iconWidth = isGreaterThanBreakpoint('md') ? '56px' : '24px';

  const mainWebinarData = get(sliceData, 'primary.main_webinar.data') || {};
  const mainWebinarTitle = RichText.asText(mainWebinarData.title || []);
  const mainWebinarDisplayDate = getFormattedDateTime(
    mainWebinarData.display_date
  );
  const mainWebinarDeck = RichText.asText(mainWebinarData.deck || []);

  const secondaryWebinarData =
    get(sliceData, 'primary.secondary_webinar.data') || {};
  const secondaryWebinarRsvpUrl =
    get(secondaryWebinarData, 'webinar_rsvp_link.url') || '';
  const secondaryWebinarDisplayDate = getFormattedDateTime(
    secondaryWebinarData.display_date
  );
  const secondaryWebinarTitle = RichText.asText(
    secondaryWebinarData.title || []
  );

  return (
    <div className="PrismicFeaturedWebinars bg-black white">
      <div className="pv10 pv20-lg relative">
        {mainWebinarData.seo_image && mainWebinarData.seo_image.url && (
          <SmartImage
            Tag="div"
            className="PrismicFeaturedWebinars__backgroundImage w-100 h-100 absolute top-0 left-0 bg-center cover o-30 z-0"
            imageOnlyClassName="PrismicFeaturedWebinars__backgroundImage o-30"
            src={mainWebinarData.seo_image.url}
          />
        )}
        <div className="container relative z-1">
          <h3 className="mb6 mb7-lg f5 f6-lg tc tl-l">
            {sliceData.primary.main_headline}
          </h3>

          <div className="row">
            <div className="mb14 mb0-lg col-lg-9">
              <div className="pr8-xl">
                <a href={getArticlePath(sliceData.primary.main_webinar)}>
                  <div className="mb4 mb0-lg aspect-ratio aspect-ratio--6x4">
                    <div className="aspect-ratio--object">
                      {mainWebinarData.seo_image &&
                        mainWebinarData.seo_image.url && (
                          <SmartImage
                            Tag="div"
                            alt={mainWebinarData.seo_image.alt}
                            className="w-100 h-100 bg-center cover br1"
                            lazy
                            src={mainWebinarData.seo_image.url}
                          />
                        )}
                      <div className="scrim--gradient-black w-100 h-100 absolute top-0 left-0 br1" />
                      <div className="w-100 ph20 transform-center-all tc">
                        <div className="mb0 mb6-lg">
                          <div className="ph6 pv6 ph12-lg pv12-lg dib relative bg-white black br-100">
                            <Icon
                              className="transform-center-all"
                              height={iconWidth}
                              name="play"
                              width={iconWidth}
                            />
                          </div>
                        </div>
                        <div className="d-none d-lg-block">
                          {mainWebinarDisplayDate && (
                            <p className="mb4">{mainWebinarDisplayDate}</p>
                          )}
                          <p className="mb4 f5 fw-medium">{mainWebinarTitle}</p>
                          <p>{mainWebinarDeck}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="d-lg-none">
                    {mainWebinarDisplayDate && (
                      <p className="mb2">{mainWebinarDisplayDate}</p>
                    )}
                    <p className="mb2 f4 fw-medium">{mainWebinarTitle}</p>
                    <p>{mainWebinarDeck}</p>
                  </div>
                </a>
              </div>
            </div>
            <div className="col-lg-3">
              <div className="pl8-lg pl0-xl">
                <p className="pb2 pb0-lg bb bw1 b--silver bn-l mb6 fw-medium f4 f3-lg tc tl-l">
                  {sliceData.primary.secondary_headline}
                </p>
                <div className="mb8 mb0-lg d-flex d-lg-block">
                  <a href={getArticlePath(sliceData.primary.secondary_webinar)}>
                    <div className="PrismicFeaturedWebinars__secondaryWebinarImage mb0 mb4-lg mr3 mr0-lg aspect-ratio aspect-ratio--6x4">
                      <div className="aspect-ratio--object">
                        {secondaryWebinarData.seo_image &&
                          secondaryWebinarData.seo_image.url && (
                            <SmartImage
                              Tag="div"
                              alt={secondaryWebinarData.seo_image.alt}
                              className="w-100 h-100 bg-center cover br1"
                              lazy
                              src={secondaryWebinarData.seo_image.url}
                            />
                          )}
                      </div>
                    </div>
                  </a>
                  <div>
                    <a
                      href={getArticlePath(sliceData.primary.secondary_webinar)}
                    >
                      {secondaryWebinarDisplayDate && (
                        <p className="d-lg-none mb1 f2">
                          {secondaryWebinarDisplayDate}
                        </p>
                      )}
                      <p className="mb4 f3-lg fw-medium">
                        {secondaryWebinarTitle}
                      </p>
                      {secondaryWebinarDisplayDate && (
                        <p className="d-none d-lg-block mb5 f2">
                          {secondaryWebinarDisplayDate}
                        </p>
                      )}
                    </a>
                    <div className="d-lg-none">
                      {secondaryWebinarRsvpUrl && (
                        <a
                          className="button button--primary dib fw-medium"
                          href={secondaryWebinarRsvpUrl}
                        >
                          RSVP
                        </a>
                      )}
                    </div>
                  </div>
                </div>
                {secondaryWebinarRsvpUrl && (
                  <a
                    className="button button--primary mt5 mb8 d-none d-lg-block fw-medium"
                    href={secondaryWebinarRsvpUrl}
                  >
                    RSVP
                  </a>
                )}
                {sliceData.primary.view_all_hub &&
                  sliceData.primary.view_all_hub.id && (
                    <div className="tc tl-l">
                      <a
                        className="pb5 bb bw1 b--white d-inline-flex d-lg-flex align-items-center justify-content-center fw-medium"
                        href={getArticlePath(sliceData.primary.view_all_hub)}
                      >
                        <span className="pr1">View all webinars</span>
                        <span>
                          <Icon name="lined-arrow-right" />
                        </span>
                      </a>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

FeaturedWebinars.propTypes = {
  sliceData: PropTypes.shape({
    primary: PropTypes.shape({
      main_headline: PropTypes.string,
      main_webinar: PropTypes.shape({
        data: PropTypes.shape({
          deck: StructuredTextPropTypes,
          display_date: PropTypes.string,
          seo: PropTypes.shape({
            alt: PropTypes.string,
            url: PropTypes.string
          }),
          title: StructuredTextPropTypes
        }).isRequired
      }).isRequired,
      secondary_headline: PropTypes.string,
      secondary_webinar: PropTypes.shape({
        data: PropTypes.shape({
          deck: StructuredTextPropTypes,
          display_date: PropTypes.string,
          seo: PropTypes.shape({
            alt: PropTypes.string,
            url: PropTypes.string
          }),
          title: StructuredTextPropTypes
        }).isRequired
      }).isRequired,
      // eslint-disable-next-line react/forbid-prop-types
      view_all_hub: PropTypes.object
    }).isRequired
  }).isRequired
};
